import { useCallback, useState } from 'react'
import { checkCustomerDniAndEmailExist } from '../../../services/SignUp'
import { showToast } from '../../molecules/Notification'
import { userDataErrorMessage } from '../../../utils/userDataErrorMessage'
import { getStoredMarket } from '../../../utils/store'
import { FormData } from './index'
import { FormGuestData } from '../FormGuest'
import { FormAuthGoogleData } from '../FormSignUpWithGoogle'
import { getGuestCustomerGroup } from '../../../services/BFF/customer-group'
import { Country, CustomerGroups } from '../../../types'

type CommonFormData = keyof FormData & keyof FormGuestData & keyof FormAuthGoogleData

interface CheckEmailProps {
  getValues: () => FormData | FormGuestData | FormAuthGoogleData
  setError: (name: CommonFormData, error: { type: string; message: string }) => void
  clearErrors: (name?: CommonFormData | undefined) => void
  documentType?: string
}

const useCheckEmailBolivia = ({ getValues, setError, clearErrors, documentType }: CheckEmailProps) => {
  const currentMarket = getStoredMarket()

  const [isChecking, setIsChecking] = useState(false)
  const [isGuestBlocked, setIsGuestBlocked] = useState(false)

  const clearEmailAndDniErrors = useCallback(() => {
    clearErrors('email' as CommonFormData)
    if (documentType !== 'nit') {
      clearErrors('dni')
    }
  }, [clearErrors, documentType])

  const handleCoincidence = useCallback(
    (response) => {
      const errorData = userDataErrorMessage({
        isGuest: response.data.isGuest,
        emailCoincidence: response.data.emailCoincidence,
        dniCoincidence: response.data.dniCoincidence,
        country: currentMarket,
      })

      if (errorData) {
        showToast({
          content: errorData.content,
          title: errorData.title,
          type: 'error',
        })

        if (response.data.emailCoincidence || response.data.dniCoincidence) {
          setError(response.data.emailCoincidence ? ('email' as CommonFormData) : 'dni', {
            type: 'manual',
            message: errorData.message,
          })
        }

        if (response.data.isGuest && response.data.dniCoincidence && response.data.emailCoincidence) {
          clearEmailAndDniErrors()
        }
      }
    },
    [setError, currentMarket, clearEmailAndDniErrors],
  )

  const checkEmailBolivia = useCallback(async () => {
    setIsChecking(true)
    setIsGuestBlocked(false)
    const formValues = getValues()

    try {
      const response = await checkCustomerDniAndEmailExist({
        dni: (formValues as FormData | FormGuestData).dni,
        email: (formValues as FormData | FormGuestData).email,
      })
      if (response.status === 200) {
        if (response.data.customerExists) {
          handleCoincidence(response)
          if (!response.data.isGuest) return
        }
        if (!response.data.customerExists) {
          clearEmailAndDniErrors()
        }
      }

      const guestGroup = await getGuestCustomerGroup(Country.BOLIVIA, (formValues as FormData | FormGuestData).email)
      if (guestGroup?.name === CustomerGroups.BLOQUEADO) {
        setIsGuestBlocked(true)
      }
    } catch (err) {
      showToast({
        content: `Ha ocurrido un error en la búsqueda del email`,
        title: `Ha ocurrido un error`,
        type: 'error',
      })
    } finally {
      setIsChecking(false)
    }
  }, [getValues, handleCoincidence, clearEmailAndDniErrors])

  return {
    checkEmailBolivia,
    isCheckingBolivia: isChecking,
    setIsCheckingBolivia: setIsChecking,
    isGuestBlockedBolivia: isGuestBlocked,
  }
}

export default useCheckEmailBolivia
